import { ref, reactive, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { useSpotStore } from '@/stores/SpotStore'
import { useUserStore } from '@/stores/UserStore'
import widgetSpotFromUrl from '@/utils/widgetSpotFromUrl'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'
import { BRANDS } from '@/data/brands'
import { BRAND_LOGOS } from '@/data/brandLogos'
import useUserGlobals from '@/composables/useUserGlobals'
import useStorageSystem from '@/composables/useStorageSystem'
import {
  buildLiveWindUrl,
  buildCurrentConditionsUrl,
  buildWindStatsUrl,
  buildYesterdaysWindUrl,
  buildWindMapUrl,
  buildIframeCode
} from '@/composables/useWidgetEmbedCode'

// The spot comes straight from the URL, not $App.globals.spot: the other JWX bundles on the
// WindAlert-Web page (map, promo modal) each replace $App.globals with their own. The local
// dev page isn't routed, so it falls back to spot 411.
const URL_SPOT = widgetSpotFromUrl()

if (import.meta.env.DEV && !URL_SPOT.id && !URL_SPOT.isArbitrarySpot) URL_SPOT.id = '411'
const { $BRAND } = useConfigurationGlobals()
const { $PRIMARYACTIVITY } = useUserGlobals()

// Everything brand-specific comes from the configuration's brand (lowercased: production's
// already is, dev's isn't always)
const BRAND = ($BRAND || '').toLowerCase()

// The brand's widget host (legacy web.config <Widgets url>)
const WIDGET_URL = `https://widgets.${BRAND}.com`
// `app` value sent to the widget host: the brand name, lowercased (legacy widgetApp)
const WIDGET_APP = BRAND
const BRAND_DEFAULT_COLOR = BRANDS[BRAND]?.defaults.widgetColor ?? ''
// The brand's logos on the CDN: its small round logo (on the Download Logos button), and the
// downloadable logos and their zip (data/brandLogos)
const LOGOS_URL = `https://cdn-consumer.weatherflow.com/lifestyle/logos/${BRAND}`
const LOGO_ICON_URL = BRANDS[BRAND] ? `${LOGOS_URL}/${BRANDS[BRAND].code}-logo-circle-small.png` : ''
const BRAND_LOGO_DOWNLOADS = BRAND_LOGOS[BRAND]
  ? {
    zipFile: BRAND_LOGOS[BRAND].zip,
    zipUrl: `${LOGOS_URL}/${BRAND_LOGOS[BRAND].zip}`,
    // White logos are previewed on a darker checkerboard
    logos: BRAND_LOGOS[BRAND].files.map(file => ({ file, url: `${LOGOS_URL}/${file}`, light: file.includes('-white-') }))
  }
  : null

// The editor's basic swatches, after the brand's default color
const BASIC_COLORS = [
  { key: 'red', color: '#b91c1c' },
  { key: 'orange', color: '#c2410c' },
  { key: 'amber', color: '#b45309' },
  { key: 'green', color: '#15803d' },
  { key: 'teal', color: '#0f766e' },
  { key: 'blue', color: '#1e67dc' },
  { key: 'navy', color: '#1e3a8a' },
  { key: 'purple', color: '#6d28d9' },
  { key: 'pink', color: '#be185d' },
  { key: 'gray', color: '#595959' },
  { key: 'black', color: '#212121' }
]

// The color swatches every widget offers: the brand's default color, then the basic colors
const PRESET_COLORS = [
  { key: 'default', color: BRAND_DEFAULT_COLOR.toLowerCase() },
  ...BASIC_COLORS
]

// Spot types and providers come from the spot's first station, as in
// WindAlert-Web WFSpotDetail.getSpotType() / getProvider() (-1 when there are no stations)
const SPOT_TYPE_STATION = 1
const NOWCAST_SPOT_TYPES = [100, 101]
const PROVIDER_WEATHERFLOW = 1

// Widget size limits, from the legacy WindWidgets.aspx.js constants
const LIVE_WIND_MIN_WIDTH = 350
const LIVE_WIND_MAX_WIDTH = 900
const LIVE_WIND_MIN_HEIGHT = 245
const LIVE_WIND_MAX_HEIGHT = 900

// Legacy CC_* values, except the min height: legacy's 500 sat above the 400 default
const CURRENT_CONDITIONS_MIN_WIDTH = 250
const CURRENT_CONDITIONS_MAX_WIDTH = 600
const CURRENT_CONDITIONS_MIN_HEIGHT = 400
const CURRENT_CONDITIONS_MAX_HEIGHT = 700

const YESTERDAYS_WIND_MIN_WIDTH = 145
const YESTERDAYS_WIND_MAX_WIDTH = 900
const YESTERDAYS_WIND_MIN_HEIGHT = 245
const YESTERDAYS_WIND_MAX_HEIGHT = 500

const WIND_MAP_MIN_WIDTH = 350
const WIND_MAP_MAX_WIDTH = 900
const WIND_MAP_MIN_HEIGHT = 245
const WIND_MAP_MAX_HEIGHT = 900

// The page's widgets, in legacy page order. `icon` is its Icon name in the editor's widget
// row. `isAvailable` ports the WindWidgets.aspx rules;
// `settings` and `sizeLimits` are the legacy WindWidgets.aspx.js defaults and clamps
// (null limits = fixed size); `buildUrl` is the widget's legacy embed URL. `hidden` keeps a
// widget off the page whatever its availability. `analyticsName` is its Widget analytics parameter
// (English, whatever the page's language). In the editor, `units` are the unit
// settings the widget shows and `titleSetting` the one its title field edits: 'title' (map
// widgets' csn) or 'name' (the name shown on the others).
//
// Each widget's color, units and name follow the page's Default Settings until set in the
// editor (null / '' = the default); resetWidget() puts them back.
export const WIDGET_DEFINITIONS = [
  {
    id: 'liveWind',
    titleKey: 'widgets.liveWind.title',
    analyticsName: 'Live Wind',
    icon: 'live-wind',
    isAvailable: () => true,
    units: ['wind'],
    titleSetting: 'title',
    settings: { width: 600, height: 450, title: '', center: '', zoom: '' },
    sizeLimits: { minWidth: LIVE_WIND_MIN_WIDTH, maxWidth: LIVE_WIND_MAX_WIDTH, minHeight: LIVE_WIND_MIN_HEIGHT, maxHeight: LIVE_WIND_MAX_HEIGHT },
    iframeName: 'wind-map',
    buildUrl: buildLiveWindUrl
  },
  {
    id: 'currentConditions',
    titleKey: 'widgets.currentConditions.title',
    analyticsName: 'Current Conditions',
    icon: 'speed-gauge',
    isAvailable: () => true,
    units: ['wind', 'temp'],
    titleSetting: 'name',
    // Default size is legacy ccWidth / ccHeight
    settings: { width: 300, height: 400, name: '' },
    sizeLimits: { minWidth: CURRENT_CONDITIONS_MIN_WIDTH, maxWidth: CURRENT_CONDITIONS_MAX_WIDTH, minHeight: CURRENT_CONDITIONS_MIN_HEIGHT, maxHeight: CURRENT_CONDITIONS_MAX_HEIGHT },
    buildUrl: buildCurrentConditionsUrl
  },
  {
    id: 'windStats',
    titleKey: 'widgets.windStats.title',
    analyticsName: 'Wind Statistics',
    // The Wind Statistics widget doesn't work right now; hidden until it's fixed (then
    // remove this)
    hidden: true,
    icon: 'stats',
    isAvailable: ({ spotType }) => spotType === SPOT_TYPE_STATION,
    units: ['wind'],
    titleSetting: 'name',
    settings: { width: 650, height: 400, name: '' },
    sizeLimits: null,
    buildUrl: buildWindStatsUrl
  },
  {
    id: 'yesterdaysWind',
    titleKey: 'widgets.yesterdaysWind.title',
    analyticsName: "Yesterday's Wind",
    icon: 'wind-graph',
    isAvailable: ({ spotType }) => !NOWCAST_SPOT_TYPES.includes(spotType),
    units: ['wind'],
    titleSetting: 'name',
    settings: { width: 500, height: 300, name: '' },
    sizeLimits: { minWidth: YESTERDAYS_WIND_MIN_WIDTH, maxWidth: YESTERDAYS_WIND_MAX_WIDTH, minHeight: YESTERDAYS_WIND_MIN_HEIGHT, maxHeight: YESTERDAYS_WIND_MAX_HEIGHT },
    buildUrl: buildYesterdaysWindUrl
  },
  {
    id: 'windMap',
    titleKey: 'widgets.windMap.title',
    analyticsName: 'Map',
    icon: 'map',
    isAvailable: () => true,
    units: ['wind'],
    titleSetting: 'title',
    settings: { width: 450, height: 400, mapType: 'contrast', title: '', profileId: '', center: '', zoom: '' },
    sizeLimits: { minWidth: WIND_MAP_MIN_WIDTH, maxWidth: WIND_MAP_MAX_WIDTH, minHeight: WIND_MAP_MIN_HEIGHT, maxHeight: WIND_MAP_MAX_HEIGHT },
    iframeName: 'wind-map',
    buildUrl: buildWindMapUrl
  }
]

export const useWidgetStore = defineStore('widgetStore', () => {
  const spotStore = useSpotStore()
  const userStore = useUserStore()

  // 'idle' | 'loading' | 'ready' | 'not-found' (no spot in the URL, or none returned) |
  // 'error' (the request failed)
  const status = ref('idle')

  // Legacy reads the first station, not SpotStore's elevation-based defaultStation
  const primaryStation = computed(() => spotStore.spot?.stations?.[0] ?? null)
  const spotType = computed(() => primaryStation.value?.type ?? -1)
  const provider = computed(() => primaryStation.value?.provider ?? -1)

  const hasSpot = computed(() => status.value === 'ready' && !!spotStore.spot)

  // Page-wide defaults every widget starts from (the Default Settings toolbar)
  const defaults = reactive({
    color: BRAND_DEFAULT_COLOR,
    // Set from the user's units by initializeDefaults() once UserStore is ready
    unitsWind: null,
    unitsTemp: null,
    // Name shown on the widgets; filled with the spot's own name once the spot loads
    spotName: ''
  })

  // Each widget's own settings, keyed by widget id
  const settings = reactive(Object.fromEntries(
    WIDGET_DEFINITIONS.map(definition => [definition.id, getInitialSettings(definition)])
  ))

  // Saved in localStorage, only what the user changed:
  // - WidgetGlobalSettings: the Global Settings color / units that differ from what the page
  //   would otherwise use (the brand color, the user's units), so the rest keep following them
  // - WidgetStations: per station, its custom title and each changed widget's changed settings
  //   ({ 'spot-411': { spotName, widgets: { windMap: { mapType: 'satellite' } } } }); stations
  //   and widgets with nothing changed aren't stored
  const [savedGlobalSettings] = useStorageSystem({}, 'WidgetGlobalSettings')
  const [savedStations] = useStorageSystem({}, 'WidgetStations')

  // The Global Settings the page starts from, before anything saved (set by initializeDefaults)
  const globalDefaults = ref(null)

  // How many custom colors to keep
  const CUSTOM_COLORS_MAX = 15

  // Colors picked in the editor that aren't presets, newest first (the editor's custom swatches);
  // saved with the Global Settings
  const customColors = ref(
    (savedGlobalSettings.value?.customColors || []).filter(isHexColor).slice(0, CUSTOM_COLORS_MAX)
  )

  // Widget being customized in the editor; null = editor closed
  const activeWidgetId = ref(null)

  const availableWidgets = computed(() => {
    if (!hasSpot.value) return []

    const context = { spotType: spotType.value, provider: provider.value }

    return WIDGET_DEFINITIONS
      .filter(definition => !definition.hidden && definition.isAvailable(context))
      .map(definition => definition.id)
  })

  // Embed url, code and size for every available widget, keyed by widget id
  const embeds = computed(() => {
    if (!hasSpot.value) return {}

    const spot = spotStore.spot
    // A name only counts as custom once it differs from the spot's own name
    const customName = defaults.spotName && defaults.spotName !== spot.name ? defaults.spotName : ''

    return Object.fromEntries(availableWidgets.value.map(widgetId => {
      const definition = getDefinition(widgetId)
      const widgetSettings = settings[widgetId]

      const src = definition.buildUrl({
        ...widgetSettings,
        widgetUrl: WIDGET_URL,
        widgetApp: WIDGET_APP,
        color: widgetSettings.color || defaults.color,
        unitsWind: widgetSettings.unitsWind || defaults.unitsWind,
        unitsTemp: widgetSettings.unitsTemp || defaults.unitsTemp,
        primaryActivity: $PRIMARYACTIVITY,
        // Legacy sends the spot name field, which starts as the spot's own name
        name: widgetSettings.name || defaults.spotName || spot.name,
        // Map widgets: their own title wins, else a custom spot name (legacy only sent
        // csn once the name was edited)
        title: widgetSettings.title || customName,
        spotName: spot.name,
        spotId: spot.spot_id,
        favoriteSpotId: spot.fav_spot_id,
        lat: spot.lat,
        lon: spot.lon
      })

      const { width, height } = widgetSettings
      const code = buildIframeCode({ src, width, height, name: definition.iframeName })

      return [widgetId, { src, code, width, height }]
    }))
  })

  // Whether resetAll() has anything to undo: Global Settings that differ from the page's own,
  // a custom title, or a changed widget
  const isCustomized = computed(() => {
    const pageDefaults = globalDefaults.value

    if (!pageDefaults) return false

    return defaults.color?.toLowerCase() !== pageDefaults.color.toLowerCase()
      || defaults.unitsWind !== pageDefaults.unitsWind
      || defaults.unitsTemp !== pageDefaults.unitsTemp
      || (hasSpot.value && defaults.spotName !== spotStore.spot.name)
      || WIDGET_DEFINITIONS.some(({ id }) => Object.keys(getChangedSettings(id)).length)
  })

  // The Spot_Name analytics parameter, as ModelSelector builds it: `${name}-${spotId}` for a
  // spot, else its location
  const analyticsSpotName = computed(() => {
    const spot = spotStore.spot

    if (!spot) return ''

    return spot.name && spot.spot_id > 0 ? `${spot.name}-${spot.spot_id}` : `${spot.lat},${spot.lon}`
  })

  // Legacy "Note: Access to some Wind Widget data may require membership."
  const showMembershipNote = computed(() => hasSpot.value && provider.value === PROVIDER_WEATHERFLOW)


  // Start the defaults from the user's units, then apply the saved Global Settings. Like the
  // Complete Report, call this only after `await userStore.ready`, once UserStore has resolved
  // the units (saved values or API defaults); mph / °F are a fallback for when it has none.
  function initializeDefaults() {
    globalDefaults.value = {
      color: BRAND_DEFAULT_COLOR,
      unitsWind: userStore.units.wind || 'mph',
      unitsTemp: userStore.units.temp || 'f'
    }

    const saved = savedGlobalSettings.value || {}

    Object.assign(defaults, globalDefaults.value, {
      ...(isHexColor(saved.color) && { color: saved.color }),
      ...(saved.unitsWind && { unitsWind: saved.unitsWind }),
      ...(saved.unitsTemp && { unitsTemp: saved.unitsTemp })
    })

    watch(() => [defaults.color, defaults.unitsWind, defaults.unitsTemp], saveGlobalSettings)
  }

  // A full 6-digit hex color, as the widgets take it (a shorter one is still being typed)
  function isHexColor(color) {
    return typeof color === 'string' && /^#?[0-9a-f]{6}$/i.test(color.trim())
  }

  // Save the Global Settings that differ from the page's own (a color mid-typing, not a hex
  // color yet, keeps whatever was saved before) and the custom colors. Before
  // initializeDefaults() there are no page defaults yet, so only the custom colors change.
  function saveGlobalSettings() {
    const previous = savedGlobalSettings.value || {}

    if (!globalDefaults.value) {
      savedGlobalSettings.value = withCustomColors({ ...previous })
      return
    }

    const changed = {}

    if (!isHexColor(defaults.color)) {
      if (previous.color) changed.color = previous.color
    } else if (defaults.color.toLowerCase() !== globalDefaults.value.color.toLowerCase()) {
      changed.color = defaults.color
    }

    if (defaults.unitsWind !== globalDefaults.value.unitsWind) changed.unitsWind = defaults.unitsWind
    if (defaults.unitsTemp !== globalDefaults.value.unitsTemp) changed.unitsTemp = defaults.unitsTemp

    savedGlobalSettings.value = withCustomColors(changed)
  }

  function withCustomColors(globalSettings) {
    delete globalSettings.customColors

    if (customColors.value.length) globalSettings.customColors = [...customColors.value]

    return globalSettings
  }

  // The station's storage key: its spot or favorite spot id, else its location
  function getStationKey(spot) {
    if (spot.spot_id > 0) return `spot-${spot.spot_id}`
    if (spot.fav_spot_id > 0) return `fav-${spot.fav_spot_id}`

    return `${spot.lat},${spot.lon}`
  }

  // A widget's settings that differ from its defaults
  function getChangedSettings(widgetId) {
    const initial = getInitialSettings(getDefinition(widgetId))

    return Object.fromEntries(
      Object.entries(settings[widgetId]).filter(([key, value]) => value !== initial[key])
    )
  }

  // Put back what was saved for this station, then save its changes from here on
  function restoreStation() {
    const spot = spotStore.spot
    const stationKey = getStationKey(spot)
    const saved = savedStations.value?.[stationKey] || {}

    if (saved.spotName) defaults.spotName = saved.spotName

    Object.entries(saved.widgets || {}).forEach(([widgetId, changed]) => {
      if (!settings[widgetId]) return

      // Only settings the widget still has
      Object.entries(changed).forEach(([key, value]) => {
        if (key in settings[widgetId]) settings[widgetId][key] = value
      })
    })

    watch([settings, () => defaults.spotName], () => saveStation(stationKey, spot.name), { deep: true })
  }

  function saveStation(stationKey, spotName) {
    const widgets = Object.fromEntries(
      WIDGET_DEFINITIONS
        .map(({ id }) => [id, getChangedSettings(id)])
        .filter(([, changed]) => Object.keys(changed).length)
    )
    const entry = {
      ...(defaults.spotName && defaults.spotName !== spotName && { spotName: defaults.spotName }),
      ...(Object.keys(widgets).length && { widgets })
    }
    const stations = { ...savedStations.value }

    if (Object.keys(entry).length) stations[stationKey] = entry
    else delete stations[stationKey]

    savedStations.value = stations
  }

  function getInitialSettings(definition) {
    return { color: null, unitsWind: null, unitsTemp: null, ...definition.settings }
  }

  // Back to the page's defaults: title / name, size, color and units (the map type, profile
  // and view stay)
  function resetWidget(widgetId) {
    const { width, height, title, name } = getInitialSettings(getDefinition(widgetId))

    Object.assign(settings[widgetId], { width, height, color: null, unitsWind: null, unitsTemp: null })

    if (title !== undefined) settings[widgetId].title = title
    if (name !== undefined) settings[widgetId].name = name
  }

  // Whether any of what resetWidget() resets differs from the defaults
  function isWidgetCustomized(widgetId) {
    const { width, height, title, name } = getInitialSettings(getDefinition(widgetId))
    const current = settings[widgetId]

    return current.width !== width
      || current.height !== height
      || !!current.color
      || !!current.unitsWind
      || !!current.unitsTemp
      || (title !== undefined && current.title !== title)
      || (name !== undefined && current.name !== name)
  }

  // Everything back to the page's defaults: the Global Settings, the title and every widget's
  // settings (map type, profile and view too). The save watchers then clear what was saved for
  // the Global Settings and this station; other stations and the custom colors are kept.
  function resetAll() {
    if (globalDefaults.value) Object.assign(defaults, globalDefaults.value)

    defaults.spotName = spotStore.spot?.name ?? ''

    WIDGET_DEFINITIONS.forEach(definition => {
      Object.assign(settings[definition.id], getInitialSettings(definition))
    })
  }

  // Remember a color picked in the editor or the Global Settings for the custom swatches (newest
  // first, the last 15), and save them. Presets and partial entries aren't remembered.
  function addCustomColor(color) {
    if (!isHexColor(color)) return

    const normalized = `#${color.trim().replace(/^#/, '').toLowerCase()}`

    if (PRESET_COLORS.some(preset => preset.color === normalized)) return

    customColors.value = [normalized, ...customColors.value.filter(saved => saved !== normalized)].slice(0, CUSTOM_COLORS_MAX)

    saveGlobalSettings()
  }

  function getDefinition(widgetId) {
    return WIDGET_DEFINITIONS.find(({ id }) => id === widgetId)
  }

  function isWidgetAvailable(widgetId) {
    return availableWidgets.value.includes(widgetId)
  }

  function customize(widgetId) {
    activeWidgetId.value = widgetId
  }

  function closeEditor() {
    activeWidgetId.value = null
  }

  async function loadSpot() {
    const params = { spot_types: '1,100,101' }
    const { id, lat, lon, isSpot, isFavoriteSpot, isArbitrarySpot } = URL_SPOT
    const hasLocation = isArbitrarySpot ? !!(lat && lon) : !!id

    if (!hasLocation) {
      status.value = 'not-found'
      return
    }

    status.value = 'loading'

    try {
      let data

      if (isSpot) data = await spotStore.getSpotDetailById(id, params)
      if (isFavoriteSpot) data = await spotStore.getSpotDetailByFavoriteId(id, params)
      if (isArbitrarySpot) data = await spotStore.getSpotDetailByLatLon(`${lat}:${lon}`, params)

      // A valid response with no spots (SpotStore reports it as this error) means not found
      if (data?.error && data.message === 'No spots found') {
        status.value = 'not-found'
        return
      }

      if (data?.error) throw data

      status.value = spotStore.spot ? 'ready' : 'not-found'

      // Start the spot name field from the spot's own name, as the legacy page did, then put
      // back anything saved for this station
      if (spotStore.spot) {
        if (!defaults.spotName) defaults.spotName = spotStore.spot.name

        restoreStation()
      }
    } catch (e) {
      console.warn('There was an error retrieving the spot data.', e)

      status.value = 'error'
    }
  }

  return {
    status,
    primaryStation,
    spotType,
    provider,
    hasSpot,
    defaults,
    settings,
    customColors,
    brandDefaultColor: BRAND_DEFAULT_COLOR,
    presetColors: PRESET_COLORS,
    activeWidgetId,
    availableWidgets,
    embeds,
    showMembershipNote,
    analyticsSpotName,
    logoIconUrl: LOGO_ICON_URL,
    logoDownloads: BRAND_LOGO_DOWNLOADS,
    initializeDefaults,
    isHexColor,
    getDefinition,
    resetWidget,
    isWidgetCustomized,
    isCustomized,
    resetAll,
    addCustomColor,
    isWidgetAvailable,
    customize,
    closeEditor,
    loadSpot
  }
})
